import clsx from "clsx";
import type { FunctionComponent } from "react";
import { useMemo } from "react";

import CompanyLogo from "~/components/CompanyLogo";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import { statusVariants } from "~/components/statusDisplay/Status/statusVariants.ts";
import { useAllCompanies } from "~/modules/client/api/company/companyQueries.ts";
import type { AvatarImage } from "~/modules/image/api/avatarImage/avatarImageTypes.ts";

type Props = {
	clientFilterValue: string[]
	onChangeClientFilter: (value: string[]) => void;
};

const PendingInvoicesIndexActiveFilters: FunctionComponent<Props> = ({
	clientFilterValue,
	onChangeClientFilter,
}) => {
	const {
		data: allClientsData,
		isLoading: isLoadingAllClients,
	} = useAllCompanies();

	const clientNamesKeyed = useMemo(() => {
		if (allClientsData) {
			return allClientsData.reduce((acc, client) => {
				acc[client.id] = { displayName: client.displayName, avatarImage: client.avatarImage };
				return acc;
			}, {} as Record<string, { displayName: string, avatarImage: AvatarImage | null }>);
		}
		return {};
	}, [allClientsData]);

	if (clientFilterValue.length === 0) {
		return null;
	}

	return (
		<div className="flex w-full pb-2 pt-4">
			<div className="ml-4 mr-2 shrink-0 text-sm text-gray-600">Aktive Filter:</div>
			<div className="ml-1">
				<div className="-m-1 flex flex-wrap items-center gap-1">
					{clientFilterValue.map((clientId,
						index) => {
						return <span
							key={"statusFilter" + index}
							className={clsx(`inline-flex items-center gap-x-1 
										rounded-full
										py-1.5 pl-3 pr-2 
										text-sm font-medium`, statusVariants({
								theme: "white",
								borderRadius: "full",
							}))}
						>
							{isLoadingAllClients && <LoadingSpinner theme="neutral" scale={0.5}/>}
							{!isLoadingAllClients && clientNamesKeyed[clientId]?.avatarImage &&
											<CompanyLogo image={clientNamesKeyed[clientId].avatarImage}
														 sizeClassName="size-4" />}
							{!isLoadingAllClients && clientNamesKeyed[clientId]?.displayName}
							<button
								type="button"
								className="ml-1 inline-flex size-4 shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500"
								onClick={() => onChangeClientFilter([...clientFilterValue].filter(filterClientId => filterClientId !== clientId))}
							>
											<span className="sr-only">Remove filter for status option</span>
											<svg
												className="size-2"
												stroke="currentColor"
												fill="none"
												viewBox="0 0 8 8"
											>
												<path strokeLinecap="round"
													  strokeWidth="1.5"
													  d="M1 1l6 6m0-6L1 7" />
											</svg>
										</button>
									</span>;
					})}
				</div>
			</div>
		</div>);
};

export default PendingInvoicesIndexActiveFilters;